// Guest checkout: book without leaving the page to sign up. The customer
// enters name, email and phone here; we create their Done Right Away account
// in the background (same /auth/register the signup page uses, no password)
// and the payment step appears right away. They can set a password later
// with "Forgot password" on the login page. An email that already has an
// account is asked to log in instead (we never sign anyone in by email alone).
import { Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ButtonComp from "../common/ButtonComp";
import CustomPhoneInput from "../common/CustomPhoneInput/CustomPhoneInput";
import { signup_service_auth } from "../../services/authentication";
import { setAuthState, setIdleRegisterStatus } from "../../store/slices/user_auth_slice";
import { SERVICE_DETAIL_PAGE_CONSTANT } from "../../store/constants";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function GuestCheckout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState(false);
  const [serverError, setServerError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const rememberBooking = () => {
    localStorage.setItem(SERVICE_DETAIL_PAGE_CONSTANT.from, "FROM_SERVICE_DETAIL");
    localStorage.setItem(SERVICE_DETAIL_PAGE_CONSTANT.service_id, id);
  };
  const goLogin = () => { rememberBooking(); navigate("/login", { state: { user_role: 3 } }); };

  const submit = async () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Enter your first name.";
    if (!form.last_name.trim()) e.last_name = "Enter your last name.";
    if (!EMAIL_RE.test(form.email.trim())) e.email = "Enter a valid email.";
    if (String(form.phone || "").replace(/\D/g, "").length < 10) e.phone = "Enter a valid phone number.";
    setErrors(e);
    setServerError("");
    setExisting(false);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    try {
      await dispatch(signup_service_auth({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: String(form.phone).replace(/\D/g, ""),
        user_role: 3,
      })).unwrap();
      dispatch(setIdleRegisterStatus());
      // Signed in now: the payment step below takes over.
      dispatch(setAuthState(true));
    } catch (err) {
      const msg = String(err?.message || "");
      if (/already been taken|already exists|already registered/i.test(msg)) setExisting(true);
      else setServerError(msg || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = (k, label, props = {}) => (
    <Stack flex={1} minWidth={0}>
      <TextField
        size="small"
        label={label}
        value={form[k]}
        onChange={set(k)}
        error={!!errors[k]}
        helperText={errors[k] || ""}
        fullWidth
        {...props}
      />
    </Stack>
  );

  return (
    <Stack mt={3} gap={2} sx={{ maxWidth: 560 }}>
      <Stack gap={0.5}>
        <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>Your details</Typography>
        <Typography sx={{ fontSize: "14px", color: "#6a6a6a" }}>
          No sign-up needed. We'll use these to confirm your booking and keep you updated.
        </Typography>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
        {field("first_name", "First name", { autoComplete: "given-name" })}
        {field("last_name", "Last name", { autoComplete: "family-name" })}
      </Stack>
      {field("email", "Email", { type: "email", autoComplete: "email" })}
      <Stack>
        <CustomPhoneInput value={form.phone} onChange={set("phone")} error={errors.phone || ""} />
        {errors.phone && <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>{errors.phone}</Typography>}
      </Stack>

      {existing && (
        <Stack sx={{ border: "1px solid #ff9800", borderRadius: "8px", p: "10px 12px", backgroundColor: "#fff8e1" }} gap={1}>
          <Typography sx={{ fontSize: "14px" }}>
            You already have an account with this email. Log in to finish your booking. Your booking details are saved.
          </Typography>
          <Typography onClick={goLogin} sx={{ fontSize: "14px", fontWeight: 600, color: "#C53F10", cursor: "pointer", textDecoration: "underline" }}>
            Log in to continue
          </Typography>
        </Stack>
      )}
      {serverError && <Typography sx={{ color: "#d32f2f", fontSize: "13px" }}>{serverError}</Typography>}

      <ButtonComp
        onClick={() => !submitting && submit()}
        label={submitting ? "Just a moment…" : "Continue to payment"}
        style={{ width: "100%", borderRadius: "15px", background: "linear-gradient(180deg, #F15A24 0%, #C53F10 100%)", margin: "4px 0 0" }}
      />
      <Typography sx={{ fontSize: "13px", color: "#6a6a6a", textAlign: "center" }}>
        Already have an account?{" "}
        <span onClick={goLogin} style={{ color: "#C53F10", fontWeight: 600, cursor: "pointer" }}>Log in</span>
      </Typography>
      <Typography sx={{ fontSize: "12px", color: "#8a8a8a", textAlign: "center" }}>
        We'll create a Done Right Away account for you to track this booking. You can set a password anytime with "Forgot password" on the login page.
      </Typography>
    </Stack>
  );
}
